import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { API_BASE_URL } from "../config/apiconfig";

const CreateAccountPage = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    mobile: "",
    companyId: "",
    companyName: "",
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  
  const navigate = useNavigate();
  const { register, login } = useAuth();

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/companies`);
      if (response.ok) {
        const companiesData = await response.json();
        setCompanies(companiesData);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  useEffect(() => {
    if (companySearchTerm.length >= 2) {
      const filtered = companies.filter(company =>
        company.company_name.toLowerCase().includes(companySearchTerm.toLowerCase())
      );
      setFilteredCompanies(filtered);
      setShowCompanyDropdown(true);
    } else {
      setFilteredCompanies([]);
      setShowCompanyDropdown(false);
    }
  }, [companySearchTerm, companies]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCompanySearch = (e) => {
    const value = e.target.value;
    setCompanySearchTerm(value);
    setFormData({ ...formData, companyName: value, companyId: "" });
  };

  const handleCompanySelect = (company) => {
    setFormData({
      ...formData,
      companyId: company.id,
      companyName: company.company_name
    });
    setCompanySearchTerm(company.company_name);
    setShowCompanyDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!formData.companyId) {
      setError("Please select a valid company");
      return;
    }

    setIsLoading(true);
    try {
      // Register the user
      await register({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        mobile: formData.mobile,
        companyId: formData.companyId,
        companyName: formData.companyName
      });
      
      setSuccess("Account created successfully! Logging you in...");
      
      // Automatically login after successful registration
      await login(formData.email, formData.password, 'user');
      
      // Navigate to dashboard
      navigate("/dashboard");
      
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-6 bg-gradient-to-r from-blue-900 to-blue-700">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-white mb-6 text-center">Create Account</h1>

        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-xl shadow-lg">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg mb-6">
              {error}
            </div>
          )}
          
          {success && (
            <div className="bg-green-50 border border-green-200 text-green-600 p-4 rounded-lg mb-6">
              {success}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block mb-2">Full Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your full name"
              />
            </div>

            <div>
              <label className="block mb-2">Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label className="block mb-2">Mobile Number</label>
              <input
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleInputChange}
                required
                className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter mobile number"
              />
            </div>

            <div className="relative">
              <label className="block mb-2">Company *</label>
              <input
                type="text"
                value={companySearchTerm}
                onChange={handleCompanySearch}
                required
                className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Type to search for your company"
                autoComplete="off"
              />
              {showCompanyDropdown && filteredCompanies.length > 0 && (
                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-40 overflow-y-auto mt-1">
                  {filteredCompanies.map((company) => (
                    <div
                      key={company.id}
                      className="p-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                      onClick={() => handleCompanySelect(company)}
                    >
                      {company.company_name}
                    </div>
                  ))}
                </div>
              )}
              {companySearchTerm.length >= 2 && filteredCompanies.length === 0 && (
                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg shadow-lg mt-1 p-3 text-gray-500">
                  No companies found
                </div>
              )}
            </div>

            <div>
              <label className="block mb-2">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter password"
              />
            </div>

            <div>
              <label className="block mb-2">Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required
                className="border p-3 w-full rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Confirm password"
              />
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <button 
              type="submit"
              disabled={isLoading}
              className="bg-blue-600 text-white font-bold p-3 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
            >
              {isLoading ? "Creating Account..." : "Sign Up"}
            </button>
          </div>
        </form>

        <div className="mt-6 text-center">
          <p className="text-white">
            Already have an account?{" "}
            <button
              onClick={() => navigate("/")}
              className="text-white hover:text-blue-200 font-medium underline"
            >
              Sign In
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CreateAccountPage;